import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import posthog from "posthog-js";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { IncompleteVibePrompt } from "./IncompleteVibePrompt";
import { PostCheckInVibeDialog } from "./PostCheckInVibeDialog";
import { useBottomPromptSlot } from "@/components/BottomPromptSlot";

interface IncompleteCheckIn {
  id: string;
  cafe_id: string;
  cafe_name: string;
  checked_in_at: string;
}

interface IncompleteVibeContextValue {
  hasIncompleteCheckIn: boolean;
  isShowingPrompt: boolean;
  isActive: boolean;
  recentlyCompletedCafeId: string | null;
  triggerPrompt: () => void;
}

const IncompleteVibeContext = createContext<IncompleteVibeContextValue>({
  hasIncompleteCheckIn: false,
  isShowingPrompt: false,
  isActive: false,
  recentlyCompletedCafeId: null,
  triggerPrompt: () => {},
});

export function useIncompleteVibe() {
  return useContext(IncompleteVibeContext);
}

interface IncompleteVibePromptProviderProps {
  children: ReactNode;
}

/** Wrapper that registers with the bottom prompt slot */
function IncompleteVibeSlottedPrompt({
  checkInId,
  cafeId,
  ...props
}: { cafeName: string; checkInId: string; cafeId: string; onTap: () => void; onDismiss: () => void }) {
  const { register, unregister, isVisible } = useBottomPromptSlot();

  useEffect(() => {
    register("incomplete-vibe");
    return () => unregister("incomplete-vibe");
  }, [register, unregister]);

  const visible = isVisible("incomplete-vibe");

  // Analytics only: fires when the ask is actually surfaced to the user.
  const shownForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) return;
    if (shownForRef.current === checkInId) return;
    shownForRef.current = checkInId;
    try {
      posthog.capture("vibe_report_prompt_shown", {
        source: "incomplete_vibe",
        check_in_id: checkInId,
        cafe_id: cafeId,
      });
    } catch { /* ignore */ }
  }, [visible, checkInId, cafeId]);

  if (!visible) return null;

  return <IncompleteVibePrompt {...props} />;
}

export function IncompleteVibePromptProvider({ children }: IncompleteVibePromptProviderProps) {
  const { user } = useAuth();
  const [hasShownThisSession, setHasShownThisSession] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedCheckIn, setSelectedCheckIn] = useState<IncompleteCheckIn | null>(null);
  const [recentlyCompletedCafeId, setRecentlyCompletedCafeId] = useState<string | null>(null);

  // Query for incomplete vibe check-ins within the last 24 hours
  const { data: incompleteCheckIn, refetch } = useQuery({
    queryKey: ["incomplete-vibe-checkin", user?.id],
    queryFn: async (): Promise<IncompleteCheckIn | null> => {
      if (!user?.id) return null;

      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from("check_ins")
        .select(`
          id,
          cafe_id,
          checked_in_at,
          cafes!inner(name)
        `)
        .eq("user_id", user.id)
        .gt("checked_in_at", twentyFourHoursAgo)
        .eq("is_complete", false)
        .order("checked_in_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("[IncompleteVibePromptProvider] Query error:", error);
        return null;
      }

      if (!data) return null;

      // Type assertion for the joined data
      const cafeData = data.cafes as unknown as { name: string };

      return {
        id: data.id,
        cafe_id: data.cafe_id,
        cafe_name: cafeData.name,
        checked_in_at: data.checked_in_at,
      };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });

  // Show prompt when we detect an incomplete check-in (once per session)
  useEffect(() => {
    if (incompleteCheckIn && !hasShownThisSession && !showDialog) {
      console.log("[IncompleteVibePromptProvider] Showing prompt for check-in:", incompleteCheckIn.id);
      setSelectedCheckIn(incompleteCheckIn);
      setShowPrompt(true);
      setHasShownThisSession(true);
    }
  }, [incompleteCheckIn, hasShownThisSession, showDialog]);

  const handlePromptTap = () => {
    setShowPrompt(false);
    setShowDialog(true);
  };

  const handlePromptDismiss = () => {
    console.log("[IncompleteVibePromptProvider] Prompt dismissed");
    setShowPrompt(false);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // Track that we just completed a check for this cafe
      if (selectedCheckIn) {
        setRecentlyCompletedCafeId(selectedCheckIn.cafe_id);
        // Clear after 30 seconds so future proximity prompts work
        setTimeout(() => setRecentlyCompletedCafeId(null), 30000);
      }
      setShowDialog(false);
      setSelectedCheckIn(null);
      // Refetch to check if the vibe was completed
      refetch();
    }
  };

  const triggerPrompt = () => {
    if (incompleteCheckIn) {
      setSelectedCheckIn(incompleteCheckIn);
      setShowPrompt(true);
    }
  };

  return (
    <IncompleteVibeContext.Provider
      value={{
        hasIncompleteCheckIn: !!incompleteCheckIn,
        isShowingPrompt: showPrompt,
        isActive: showPrompt || showDialog,
        recentlyCompletedCafeId,
        triggerPrompt,
      }}
    >
      {children}

      {/* Bottom banner prompt - uses slot system */}
      {showPrompt && selectedCheckIn && (
        <IncompleteVibeSlottedPrompt
          cafeName={selectedCheckIn.cafe_name}
          checkInId={selectedCheckIn.id}
          cafeId={selectedCheckIn.cafe_id}
          onTap={handlePromptTap}
          onDismiss={handlePromptDismiss}
        />
      )}

      {/* Vibe dialog */}
      {selectedCheckIn && (
        <PostCheckInVibeDialog
          checkInId={selectedCheckIn.id}
          cafeId={selectedCheckIn.cafe_id}
          cafeName={selectedCheckIn.cafe_name}
          open={showDialog}
          onOpenChange={handleDialogClose}
        />
      )}
    </IncompleteVibeContext.Provider>
  );
}
